import { useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Button, Card, Group, Stack, Text, TextInput } from "@mantine/core";
import { toast } from "sonner";
import { tenantApi } from "@/lib/api/client";

export const DOOR_PIN_STATUS_KEY = ["door-pin-status"] as const;

export interface DoorPinSectionProps {
  /** Called once a new PIN is saved (the Door PIN guide ticks its step). */
  onSaved?: () => void;
  /** Shown instead of the card when the member can't set a PIN. The account
   *  page passes nothing: the card is simply absent there. */
  unavailable?: ReactNode;
  /** Shown when the PIN status can't be loaded. The account page passes nothing. */
  loadError?: ReactNode;
}

/**
 * The member's own keypad PIN (Vaka personal code) — the Account page's card,
 * also the `door.pin` guide block (#436). Only shown when the member has a
 * linked access-control profile and PINs are enabled.
 */
export function DoorPinSection({ onSaved, unavailable = null, loadError = null }: DoorPinSectionProps) {
  const [pin, setPin] = useState("");
  const { data: status, isLoading, isError } = useQuery({
    queryKey: DOOR_PIN_STATUS_KEY,
    queryFn: () => tenantApi.getDoorPinStatus().then((r) => r.data),
  });
  const mutation = useMutation({
    mutationFn: (value: string) => tenantApi.setDoorPin(value).then((r) => r.data),
    onSuccess: () => {
      toast.success("Door PIN updated");
      setPin("");
      onSaved?.();
    },
    onError: (err: unknown) => {
      Sentry.captureException(err);
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || "Failed to update PIN");
    },
  });

  if (isError) return <>{loadError}</>;
  if (!status?.available) return isLoading ? null : <>{unavailable}</>;
  const length = status.length;
  const valid = /^\d+$/.test(pin) && pin.length === length;

  return (
    <Card withBorder padding="lg">
      <Stack gap="xs" mb="sm">
        <Text fw={500}>Door PIN</Text>
        <Text fz="xs" c="dimmed">
          Set the {length}-digit code you type on the building keypads. The previous code stops working immediately.
        </Text>
      </Stack>
      <Group gap="sm" align="flex-end">
        <TextInput
          label={`New PIN (${length} digits)`}
          value={pin}
          onChange={(e) => setPin(e.currentTarget.value.replace(/\D/g, "").slice(0, length))}
          placeholder={"•".repeat(length)}
          inputMode="numeric"
          error={pin.length > 0 && !valid ? `Must be ${length} digits` : undefined}
          flex={1}
        />
        <Button size="sm" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate(pin)}>
          {mutation.isPending ? "Saving..." : "Update PIN"}
        </Button>
      </Group>
    </Card>
  );
}
